import React, { useState, useRef, useEffect } from "react";
import "pages/Teleconsult/MentalHealthInfo.scss";
import IconMood from "assets/icons/MoodIcon.svg";
import { Modal, Container, Row, Col } from "react-bootstrap";
import MentalHealthService from "services/mental-health.service";
import AuthService from 'services/auth.service';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, defs, LinearGradient, stop } from "recharts";
import IconDropdown from "assets/icons/dropdownIcon.svg";
import Spinner from "react-bootstrap/Spinner";
import IconArrowDown from "assets/icons/expandAllIcon.svg";
import moment from 'moment';

import DayPickerInput from "react-day-picker/DayPickerInput";
import CalendarIcon from 'assets/icons/IconAssessmentCalendar.svg';
import { parseDate } from 'react-day-picker/moment';
import 'react-day-picker/lib/style.css';

const ModalMoodLog = React.memo(({ chat }) => {
  const today = new Date();
  const thisYear = new Date().getFullYear();
  const [show, setShow] = useState(false);
  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);
  const [moodCount, setMoodCount] = useState(null);
  const isServiceCancelled = useRef(false);
  const [year, setYear] = useState(thisYear);
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [moodMapping, setMoodMapping] = useState({});
  const [graphData, setGraphData] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [loader, setLoader] = useState(false);
  const [showFilter, setShowFilter] = useState(false);
  const handleCloseFilter = () => setShowFilter(false);
  const handleShowFilter = () => setShowFilter(true);
  const years = Array.from({ length: 5 }, (_, i) => thisYear - i)
  const months = Array.from({ length: 12 }, (_, i) => moment().month(i).format("MMMM"));
  const [highestMoodValue, setHighestMoodValue] = useState(0);

  useEffect(() => {
    setLoader(true);
    const params = {
      user_id: chat?.user_info?.id,
      year: year,
      month: month
    }
    MentalHealthService.getMoodInsights(params).then(
      (data) => {
        setMoodCount(data?.mood_count);
        const sortedEmotions = [...data?.mood_count].sort((a, b) => b.sequence - a.sequence);
        const emotionToValue = sortedEmotions.reduce((acc, emotion, index) => {
          acc[emotion.id] = index + 1;
          return acc;
        }, {});
        setMoodMapping(emotionToValue);
        const transformedData = data?.moods.map(item => ({
          ...item,
          date: new Date(item.date).getDate(),
          moodValue: emotionToValue[item.emotion_id],
        }));
        setHighestMoodValue(transformedData.reduce((max, item) => (item.moodValue > max.moodValue ? item : max), transformedData[0])?.emotion_id || 0);
        setGraphData(generateFullMonthData(month, year, transformedData))
      },
      (error) => {
        if (error.response.status === 401) {
          AuthService.logout();
          window.location.replace('/eb/login');
        }
      }
    ).then(() => {
      setLoader(false);
    });
    return () => {
      isServiceCancelled.current = true;
    };
  }, [year, month]); // eslint-disable-line


  const generateFullMonthData = (month, year, transformedData) => {
    const daysInMonth = new Date(year, month-1, 0).getDate();
    const fullData = [];
    for (let day = 1; day <= daysInMonth; day++) {
      let future_date = new Date(year, month-1, day) > today
      var existing = transformedData.find((d) => d.date === day);
      fullData.push(existing || { date: day, moodValue: future_date ? null : 0, color_code: 'transparent' });
    }
    return fullData;
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const moodInfo = payload[0].payload;
      if (!moodInfo.id) return null; 

      return (
        <div className="mood-details">
          <div><strong>{moodInfo.name}</strong></div>
          <ul>
            {moodInfo?.questionnaires.map((mood, index) => (
              (mood.label && mood.answer) ? <li key={`mood-${index}`}>{mood.label}: {ToolTipContent(mood)}</li> : null
            ))}
          </ul>
        </div>
      );
    }
    return null;
  };

  const ToolTipContent = (mood) => {
    switch(mood.question_type){
      case 'rate':
        return `${mood.answer}/5`;
      case 'mapping':
      case 'map':
        const answer = mood.answer;
        let formatted_answer = '';
        mood.answer.split(";").forEach((part) => {
          let [key, value] = part.split(" - ");
          if (!key || !value) {
            return mood.answer;
          }
          formatted_answer = [formatted_answer, value.trim()].filter(Boolean).join(',');
        });
        return formatted_answer;
      default:
        return mood.answer;
    }
  }

  const CustomYAxisTick = (props) => {
    const { x, y, payload, moodCount } = props;
    const moodImage = moodCount.find(emo => moodMapping[emo.id] === payload.value)?.icon?.url;
    if (!moodImage) { 
      return null;
    }
    return (
      <g><image href={moodImage} x={x - 30} y={y - 15} width="30" height="30" /></g>
    );
  };

  const MoodChart = () => {
    return (
      <ResponsiveContainer width="100%" height={400}>
        <LineChart data={graphData} margin={{ top: 15, right: 10 }}>
          <defs>
            <linearGradient id="colorMood" x1="0" y1="0" x2="0" y2="1">
              {moodCount.filter((m,index) => (moodCount.length-moodMapping[highestMoodValue]) <= index).map((color, index) => (
                <stop key={index} offset={`${(index / moodCount.length) * 100}%`} stopColor={color.color_code} stopOpacity={1} />
              ))}
              {graphData.find(d => d.moodValue === 0) && <stop offset="100%" stopColor="#808080" stopOpacity={1} />}
            </linearGradient>
          </defs>
  
          <CartesianGrid strokeDasharray="0 0 " />

          <XAxis dataKey="date" tick={{ fill: "#4E4E4E", fontSize: 12, fontWeight: 500 }} interval={0} minTickGap={0}/>
  
          <YAxis domain={[0, moodCount.length]} ticks={[...Array(moodCount.length - 0 + 1).keys()]} tickFormatter={(value) => {
            return moodCount.find(emo => moodMapping[emo.id] === value);
          }} tick={<CustomYAxisTick moodCount={moodCount} />}/>
  
          <Tooltip content={<CustomTooltip />} />
  
          <Line
            type="linear"
            dataKey="moodValue"
            stroke="url(#colorMood)"
            strokeWidth={3}
            dot={({ cx, cy, payload }) => (
              <circle cx={cx} cy={cy} r={5} fill={payload.color_code} />
            )}
            connectNulls={true}
            activeDot={({ cx, cy, payload }) => (
              <circle cx={cx} cy={cy} r={8} fill={payload.color_code} />
            )}
          />
        </LineChart>
      </ResponsiveContainer>
    );
  };

  const FilterModal = ({}) => {  
    const [yearInput, setYearInput] = useState(year);
    const [monthInput, setMonthInput] = useState(month);

    const handleSaveFilter = (e) => {
      handleCloseFilter();
      setMonth(monthInput);
      setYear(yearInput);
    }

    return (
      <Modal className="filter-modal" show={showFilter} onHide={handleCloseFilter} backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>Filters</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="date-inputs">
            <div className="d-sm-flex align-items-end w-100">
            <div className="start-date-container">
              <p className="mb-1 text-lightgrey font-size-4">Month</p>
              <span className="daypicker-container">
                <img src={ CalendarIcon } alt="calendar-icon" className="calendar-icon" />
                <select className="month-year-input" value={monthInput} onChange={(e) => setMonthInput(e.target.value) }>
                  {months.map((opt,index) => (
                    <option key={opt} value={index+1}>{opt}</option>
                  ))}
                </select>
              </span>
            </div>
            <span className="d-none d-sm-inline-block mb-2 mx-1 font-size-11 fweight-300">-</span>
            <div className="end-date-container">
              <p className="mb-1 text-lightgrey font-size-4">Year</p>
              <span className="daypicker-container">
                <img src={ CalendarIcon } alt="calendar-icon" className="calendar-icon" />
                <select className="month-year-input" value={yearInput} onChange={(e) => setYearInput(e.target.value) }>
                  {years.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </span>
            </div>
          </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button className="btn save-filter-btn" onClick={handleSaveFilter}>
            Filter
          </button>
        </Modal.Footer>
      </Modal>
    );
  };

  return (
    <>
      <button className="btn mood-log-btn" onClick={handleShow}>
        <div><img src={IconMood} alt="mood" className="mood-log-icon"/>Mood Insights</div>
        <div><img src={IconArrowDown} alt="icon-arrow-down" /></div>
      </button>
      <Modal show={show} onHide={handleClose} size='lg' className="mood-log-modal">
        <Modal.Header closeButton>
          <Modal.Title>Mood Insights</Modal.Title>
          <div className="month-picker" onClick={handleShowFilter}>
            <div className="month-display">{months[month-1]} {year} </div>
            <div className="btn filter-btn">
              <img src={IconDropdown} alt="filter" />
            </div>
          </div>
        </Modal.Header>
        <div className="loader conditional-display" displayed={loader.toString()}>
          <Spinner animation="border" className="spinner" />
        </div>
        <Modal.Body className="conditional-display" displayed={(!loader).toString()}>
          {moodCount && <MoodChart /> }
          <Container>
            <Row>
              {moodCount?.map((mood_count) => (
                <Col sm={4} md={4} className="mood-col">
                  <div className="mood-name" style={{color: mood_count.color_code}}>{mood_count.name}</div>
                  <div className="mood-count">{mood_count.percentage}<span className="percentage">%</span></div>
                </Col>
              ))}
              </Row>
          </Container>
        </Modal.Body>
      </Modal>
      <FilterModal />
    </>
  );
});

export default ModalMoodLog;